import { useEffect, useState, useSyncExternalStore } from "react";

interface Controller<State> {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => State;
}

export function useController<State>(controller: Controller<State>): State {
  return useSyncExternalStore(controller.subscribe, controller.getSnapshot, controller.getSnapshot);
}

interface ToggleProps {
  id?: string;
  on: boolean;
  onChange: (value: boolean) => void;
  ariaLabel: string;
}

export function Toggle({ id, on, onChange, ariaLabel }: ToggleProps) {
  return <button
    id={id}
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={ariaLabel}
    className={`ios-toggle${on ? " is-on" : ""}`}
    onClick={() => onChange(!on)}
  >
    <span className="ios-toggle-knob" aria-hidden />
  </button>;
}

interface TokenMarkProps {
  className: string;
  image: string | null;
  symbol: string;
}

export function TokenMark({ className, image, symbol }: TokenMarkProps) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  return <span className={className}>
    {image !== null && failedImage !== image
      ? <img src={image} alt="" onError={() => setFailedImage(image)} />
      : symbol.slice(0, 2)}
  </span>;
}

export function SecretNote({ value }: { value: string }) {
  const [copiedValue, setCopiedValue] = useState<string | null>(null);
  const copied = copiedValue === value;
  useEffect(() => {
    if (copiedValue === null) return;
    const timeout = window.setTimeout(() => setCopiedValue(null), 1_600);
    return () => window.clearTimeout(timeout);
  }, [copiedValue]);

  return <button
    className={`secret-note${copied ? " copied" : ""}`}
    type="button"
    aria-label={copied ? "Copied" : "Copy secret note"}
    onClick={() => void navigator.clipboard.writeText(value).then(() => setCopiedValue(value)).catch(() => undefined)}
  >
    {copied && <span className="secret-note-feedback">Copied</span>}
    <code>{value}</code>
  </button>;
}
